// Landing page — pinned reports (main area) + resizable resource drawer
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Box, CPText, List } from "shared/components/base";
import CPFontAwesome from "shared/components/shared/CPFontAwesome/CPFontAwesome";
import CPLoader from "shared/components/shared/CPLoader/CPLoader";
import CPPowerBIReport from "shared/components/shared/CPPowerBIReport/CPPowerBIReport";
import CPResizableBottomDrawer from "shared/components/shared/CPResizableBottomDrawer/CPResizableBottomDrawer";
import { useCurrentUserQuery } from "data/user/user";
import {
  useGetPinnedReportsQuery,
  useUnpinReportMutation,
  PinnedReport,
} from "data/pins/pins";
import { useGetResourceDetailQuery } from "data/resources/resource";
import "./DashboardPageStyles.scss";
import CPTabbedStrip, {
  CPTabbedStripTab,
} from "shared/components/shared/CPTabbedStrip/CPTabbedStrip";
import CPSpacer from "shared/components/shared/CPSpacer/CPSpacer";

// Constants

const RESOURCE_TYPE = {
  REPORT: "REPORT",
  LINK: "LINK",
  FILE: "FILE",
  PAGE: "PAGE",
} as const;

const TYPE_META: Record<string, { icon: string; label: string }> = {
  REPORT: { icon: "fa-solid fa-chart-column", label: "Report" },
  LINK: { icon: "fa-solid fa-arrow-up-right-from-square", label: "Link" },
  FILE: { icon: "fa-solid fa-file-arrow-down", label: "Download" },
  PAGE: { icon: "fa-solid fa-file-lines", label: "Page" },
};

// Lazy link/file action

interface LazyResourceActionProps {
  resourceId: number;
  resourceType: string;
  onDone: () => void;
}

const LazyResourceAction: React.FC<LazyResourceActionProps> = ({
  resourceId,
  resourceType,
  onDone,
}) => {
  const { data, isLoading } = useGetResourceDetailQuery(resourceId);

  React.useEffect(() => {
    if (!data) return;
    if (resourceType === RESOURCE_TYPE.LINK) {
      const url = data.url;
      if (url) {
        const mode = data.mode?.toUpperCase();
        if (mode === "TAB" || mode === "WINDOW") {
          window.open(url, "_blank", "noopener,noreferrer");
        } else {
          window.location.href = url;
        }
      }
    } else if (resourceType === RESOURCE_TYPE.FILE) {
      const url = data.downloadURL;
      if (url) {
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = data.fileName ?? "";
        anchor.rel = "noopener noreferrer";
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      }
    }
    onDone();
  }, [data]);

  if (isLoading) return <CPLoader isLoading />;
  return null;
};

// Dashboard

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: user } = useCurrentUserQuery();
  const userId = user?.userId ?? 0;

  const { data: pinnedReports = [] } = useGetPinnedReportsQuery(userId);
  const unpinMutation = useUnpinReportMutation(userId);

  const [closedPinIds, setClosedPinIds] = React.useState<Set<number>>(
    new Set(),
  );
  const [activeReportId, setActiveReportId] = React.useState<number | null>(
    null,
  );
  const [pendingAction, setPendingAction] = React.useState<{
    resourceId: number;
    type: string;
  } | null>(null);

  // Drawer: start expanded; collapse once pinned reports arrive
  const [drawerExpanded, setDrawerExpanded] = React.useState(true);
  const drawerInitializedRef = React.useRef(false);

  const agencyCodes = React.useMemo(
    () => user?.agencies?.map((a) => a.code) ?? [],
    [user?.agencies],
  );

  const visiblePins = React.useMemo(
    () => pinnedReports.filter((r) => !closedPinIds.has(r.resourceId)),
    [pinnedReports, closedPinIds],
  );

  // Auto-select first visible pin when the list changes
  React.useEffect(() => {
    if (visiblePins.length === 0) {
      setActiveReportId(null);
      return;
    }
    setActiveReportId((prev) => {
      if (prev !== null && visiblePins.some((r) => r.resourceId === prev))
        return prev;
      return visiblePins[0].resourceId;
    });
  }, [visiblePins]);

  // Set drawer default state once data is available: collapsed when pins exist
  React.useEffect(() => {
    if (drawerInitializedRef.current) return;
    if (userId === 0) return;
    drawerInitializedRef.current = true;
    setDrawerExpanded(pinnedReports.length === 0);
  }, [userId, pinnedReports]);

  const selectedTabIndex = React.useMemo(() => {
    if (activeReportId === null || visiblePins.length === 0) return 0;
    const idx = visiblePins.findIndex((r) => r.resourceId === activeReportId);
    return idx >= 0 ? idx : 0;
  }, [activeReportId, visiblePins]);

  const categories = React.useMemo(
    () =>
      (user?.categoryResources ?? []).filter(
        (c) => c.resources.length > 0 && c.parentCategory === null,
      ),
    [user?.categoryResources],
  );

  const hasReportResources = React.useMemo(
    () =>
      (user?.categoryResources ?? []).some((c) =>
        c.resources.some((r) => r.resourceType === RESOURCE_TYPE.REPORT),
      ),
    [user?.categoryResources],
  );

  const handleCloseTab = React.useCallback((resourceId: number) => {
    setClosedPinIds((prev) => new Set(Array.from(prev).concat(resourceId)));
  }, []);

  const handleUnpinTab = React.useCallback(
    (resourceId: number) => {
      unpinMutation.mutate(resourceId);
      handleCloseTab(resourceId);
    },
    [unpinMutation, handleCloseTab],
  );

  const handleResourceClick = (
    resourceId: number,
    resourceType: string,
    categoryId: number,
    url?: string | null,
  ) => {
    if (resourceType === RESOURCE_TYPE.PAGE && url) {
      navigate(url);
    } else if (resourceType === RESOURCE_TYPE.REPORT) {
      navigate(`/category/${categoryId}/report/${resourceId}`);
    } else {
      setPendingAction({ resourceId, type: resourceType });
    }
  };

  const handleTabSelect = React.useCallback(
    (index: number) => {
      const report = visiblePins[index];
      if (report) setActiveReportId(report.resourceId);
    },
    [visiblePins],
  );

  const tabTitle = (report: PinnedReport) => (
    <span className="cp-dashboard-tab-title-wrapper">
      <span className="cp-dashboard-tab-title">{report.title}</span>
      <button
        className="cp-dashboard-tab-btn unpin"
        title="Unpin report"
        onClick={(e) => {
          e.stopPropagation();
          handleUnpinTab(report.resourceId);
        }}
      >
        <i className="fa-solid fa-thumbtack" />
      </button>
      <button
        className="cp-dashboard-tab-btn close"
        title="Close tab"
        onClick={(e) => {
          e.stopPropagation();
          handleCloseTab(report.resourceId);
        }}
      >
        <i className="fa-solid fa-xmark" />
      </button>
    </span>
  );

  const categoryGrid = (
    <Box
      className="cp-dashboard-category-grid"
      flexDirection="row"
      style={{
        flexWrap: "wrap",
        alignContent: "flex-start",
        alignItems: "flex-start",
        gap: "16px",
      }}
    >
      {categories.length === 0 ? (
        <Box className="cp-dashboard-empty-categories">
          <CPFontAwesome
            icon="fa-solid fa-triangle-exclamation"
            size={32}
            style={{ color: "#bdbdbd" }}
          />
          <CPText style={{ color: "#9e9e9e", marginTop: 10 }}>
            Not assigned any resource(s). Contact support to be assigned a
            resource.
          </CPText>
        </Box>
      ) : (
        categories.map((cat) => (
          <Box
            key={cat.categoryId}
            className="cp-dashboard-category-card"
            style={{ padding: 0 }}
          >
            <Box className="cp-dashboard-category-header">{cat.name}</Box>
            <List className="cp-dashboard-resource-list">
              {cat.resources.slice(0, 5).map((res) => {
                const meta = TYPE_META[res.resourceType] ?? TYPE_META.REPORT;
                return (
                  <li
                    key={res.resourceId}
                    className="cp-dashboard-resource-item"
                    onClick={() =>
                      handleResourceClick(
                        res.resourceId,
                        res.resourceType,
                        cat.categoryId,
                        res.url,
                      )
                    }
                    title={`${meta.label}: ${res.name}`}
                  >
                    <i className={`${meta.icon} cp-dashboard-resource-icon`} />
                    <span className="cp-dashboard-resource-name">
                      {res.name}
                    </span>
                    <span
                      className={`cp-dashboard-resource-badge badge-${res.resourceType.toLowerCase()}`}
                    >
                      {meta.label}
                    </span>
                  </li>
                );
              })}
              {cat.resources.length > 5 && (
                <li
                  className="cp-dashboard-resource-item cp-dashboard-resource-viewall"
                  onClick={() =>
                    navigate(
                      `/category/${cat.name.toLowerCase().replace(/\s+/g, "-")}`,
                    )
                  }
                >
                  <i className="fa-solid fa-list cp-dashboard-resource-icon" />
                  <span className="cp-dashboard-resource-name">View all</span>
                </li>
              )}
            </List>
          </Box>
        ))
      )}
    </Box>
  );

  return (
    <Box className="cp-dashboard">
      {/* Page header */}
      <Box className="cp-dashboard-header">
        <CPText className="cp-dashboard-header-title">Dashboard</CPText>
      </Box>

      {/* Main area */}
      <Box className="cp-dashboard-main">
        {hasReportResources ? (
          <>
            <Box className="cp-dashboard-pinned-area">
              {visiblePins.length > 0 ? (
                <CPTabbedStrip
                  selected={selectedTabIndex}
                  onSelect={handleTabSelect}
                  scrollable
                  className="cp-dashboard-tabstrip"
                >
                  {visiblePins.map((report, idx) => (
                    <CPTabbedStripTab
                      key={report.resourceId}
                      title={tabTitle(report)}
                    >
                      {selectedTabIndex === idx ? (
                        <CPPowerBIReport
                          workspaceId={report.workspaceId}
                          reportId={report.reportId}
                          datasetId={report.datasetId}
                          defaultPageName={report.defaultPageName}
                          requiresEffectiveIdentity={
                            report.requiresEffectiveIdentity
                          }
                          roles={
                            report.pbiRoles
                              ?.split(";")
                              .map((r) => r.trim())
                              .filter(Boolean) ?? []
                          }
                          defaultAgencyCode={report.defaultAgencyCode}
                          agencies={agencyCodes}
                          className="cp-dashboard-pbi"
                        />
                      ) : null}
                    </CPTabbedStripTab>
                  ))}
                </CPTabbedStrip>
              ) : (
                <Box className="cp-dashboard-placeholder">
                  <CPFontAwesome
                    icon="fa-solid fa-thumbtack"
                    size={40}
                    style={{ color: "#bdbdbd" }}
                  />
                  <CPText style={{ color: "#9e9e9e", marginTop: 10 }}>
                    Pin a report to display it here on your dashboard
                  </CPText>
                </Box>
              )}
            </Box>

            <CPSpacer height={25} />

            <CPResizableBottomDrawer
              title="Resources"
              isExpanded={drawerExpanded}
              onExpandedChange={setDrawerExpanded}
              defaultExpandedHeight={320}
            >
              {categoryGrid}
            </CPResizableBottomDrawer>
          </>
        ) : (
          categoryGrid
        )}
      </Box>

      {/* Lazy resource action for links / downloads */}
      {pendingAction && (
        <LazyResourceAction
          resourceId={pendingAction.resourceId}
          resourceType={pendingAction.type}
          onDone={() => setPendingAction(null)}
        />
      )}
    </Box>
  );
};

export default DashboardPage;
